import { supabaseClient } from 'supabase'
import type { VenueHistoryPair, VenueHistoryRow } from 'pages/Desk/venueHistory'
import { CustomVenueHistoryRpcRow } from './deskTypes'

/**
 * The grouped history of hand-typed venues in one town, from the admin-only
 * `custom_venue_history` RPC (0038, 33.3 D-02). It is grouped in SQL because
 * PostgREST caps a select at 1000 rows; the stopword merge of spellings
 * happens in the browser, in pages/Desk/venueHistory.ts.
 *
 * Throws on error, like queryDeskCounts; useCustomVenueHistory makes the
 * lookup never-throw for the desk (D-10).
 *
 * The bigint `use_count` goes through `Number()` in case it ever arrives as a
 * string. `pairs` is jsonb from the server and is read defensively: an item
 * without finite numeric lat/lng is dropped (T-33.3-19).
 */
/** PostgREST's code for "no such function in the schema cache". */
const FUNCTION_NOT_FOUND = 'PGRST202'

export const queryCustomVenueHistory = async ({
  cityId,
  cityText
}: {
  cityId: number | null
  cityText: string
}): Promise<VenueHistoryRow[]> => {
  const { data, error } = await supabaseClient.rpc('custom_venue_history', {
    p_city_id: cityId,
    p_city_text: cityText.trim() === '' ? null : cityText
  })

  if (error) {
    // Review WR-07: a database without 0038 (prod until its apply) answers
    // PGRST202, function not found. That is "no history", not a failed
    // lookup, so an admin deployed before the migration shows no
    // "could not be looked up" note on every custom-venue candidate.
    // Every other error still throws, and the desk shows the note (D-10).
    if (error.code === FUNCTION_NOT_FOUND) {
      return []
    }
    throw error
  }
  return ((data ?? []) as CustomVenueHistoryRpcRow[]).map((row) => ({
    venueName: row.venue_name ?? '',
    useCount: Number(row.use_count) || 0,
    lastUsedAt: row.last_used_at ?? null,
    pairs: readPairs(row.pairs),
    address: row.venue_address,
    description: row.venue_description,
    descriptionPt: row.venue_description_pt,
    phone: row.venue_phone,
    website: row.venue_website
  }))
}

/** Keep only pairs with finite numeric lat/lng; `uses` defaults to 1, `last_used_at` to null. */
const readPairs = (value: unknown): VenueHistoryPair[] => {
  if (!Array.isArray(value)) {
    return []
  }
  const pairs: VenueHistoryPair[] = []
  for (const item of value) {
    if (item === null || typeof item !== 'object') {
      continue
    }
    const raw = item as Record<string, unknown>
    const lat = toFiniteNumber(raw.lat)
    const lng = toFiniteNumber(raw.lng)
    if (lat === null || lng === null) {
      continue
    }
    const uses = toFiniteNumber(raw.uses)
    pairs.push({
      lat,
      lng,
      uses: uses === null ? 1 : uses,
      lastUsedAt: typeof raw.last_used_at === 'string' ? raw.last_used_at : null
    })
  }
  return pairs
}

/** A number or numeric string as a finite number; null, '', booleans and the rest are null. */
const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null
  }
  if (typeof value === 'string' && value.trim() === '') {
    return null
  }
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}
